import React, { useState, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'

/**
 * ImageResizeModal Component
 * 
 * Modal component for resizing images safely without DOM manipulation conflicts.
 * Provides size controls with real-time preview and aspect ratio preservation.
 */
const ImageResizeModal = ({ 
  isOpen, 
  onClose, 
  onApply,
  imageElement,
  currentWidth = 300,
  currentHeight = 200
}) => {
  const [width, setWidth] = useState(currentWidth)
  const [height, setHeight] = useState(currentHeight)
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true)
  const [originalAspectRatio, setOriginalAspectRatio] = useState(1)
  const [previewWidth, setPreviewWidth] = useState(300)
  const [previewHeight, setPreviewHeight] = useState(200)
  
  // Initialize values when modal opens
  useEffect(() => {
    if (isOpen && imageElement) {
      const rect = imageElement.getBoundingClientRect()
      const naturalWidth = imageElement.naturalWidth || rect.width
      const naturalHeight = imageElement.naturalHeight || rect.height
      const currentDisplayWidth = rect.width
      const currentDisplayHeight = rect.height
      
      setWidth(Math.round(currentDisplayWidth))
      setHeight(Math.round(currentDisplayHeight))
      setOriginalAspectRatio(naturalWidth / naturalHeight)
      
      // Set preview to actual pixel size (no scaling)
      setPreviewWidth(currentDisplayWidth)
      setPreviewHeight(currentDisplayHeight)
    }
  }, [isOpen, imageElement])
  
  /**
   * Handle width change with aspect ratio preservation
   */
  const handleWidthChange = useCallback((newWidth) => {
    setWidth(newWidth)
    
    if (maintainAspectRatio) {
      const newHeight = Math.round(newWidth / originalAspectRatio)
      setHeight(newHeight)
    }
    
    // Update preview
    updatePreview(newWidth, maintainAspectRatio ? newWidth / originalAspectRatio : height)
  }, [maintainAspectRatio, originalAspectRatio, height])
  
  /**
   * Handle height change with aspect ratio preservation
   */
  const handleHeightChange = useCallback((newHeight) => {
    setHeight(newHeight)
    
    if (maintainAspectRatio) {
      const newWidth = Math.round(newHeight * originalAspectRatio)
      setWidth(newWidth)
    }
    
    // Update preview
    updatePreview(maintainAspectRatio ? newHeight * originalAspectRatio : width, newHeight)
  }, [maintainAspectRatio, originalAspectRatio, width])
  
  /**
   * Update preview dimensions
   */
  const updatePreview = useCallback((newWidth, newHeight) => {
    // Show actual pixel size (no scaling)
    setPreviewWidth(newWidth)
    setPreviewHeight(newHeight)
  }, [])
  
  /**
   * Handle aspect ratio toggle
   */
  const handleAspectRatioToggle = useCallback((enabled) => {
    setMaintainAspectRatio(enabled)
    
    if (enabled) {
      // Recalculate height based on current width
      const newHeight = Math.round(width / originalAspectRatio)
      setHeight(newHeight)
      updatePreview(width, newHeight)
    }
  }, [width, originalAspectRatio, updatePreview])
  
  /**
   * Handle apply resize
   */
  const handleApply = useCallback(() => {
    if (onApply) {
      onApply(width, height)
    }
    onClose()
  }, [width, height, onApply, onClose])
  
  /**
   * Handle cancel
   */
  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])
  
  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleApply()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }, [handleApply, handleCancel])
  
  // Add keyboard event listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])
  
  if (!isOpen || !imageElement) return null
  
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60] p-4">
      <div className="relative w-full max-w-4xl h-[90vh] rounded-2xl shadow-2xl overflow-hidden">
        
        {/* Full-size Image Background */}
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-900/50 backdrop-blur-[10px] overflow-hidden">
          <img
            src={imageElement.src}
            alt="Resize Preview"
            style={{
              width: `${previewWidth}px`,
              height: `${previewHeight}px`,
              flexShrink: 0
            }}
            className="rounded-2xl"
          />
        </div>

        {/* Close Button - Top Right */}
        <button
          type="button"
          onClick={handleCancel}
          className="absolute top-4 right-4 p-3 rounded-full bg-white/50 dark:bg-black/50 text-gray-800 dark:text-white hover:bg-white/60 dark:hover:bg-black/60 backdrop-blur-[10px] transition-all duration-200 z-10"
          title="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Title - Top Left */}
        <div className="absolute top-4 left-4 flex items-center gap-3 z-10">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg backdrop-blur-[10px]">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-800 dark:text-white drop-shadow-lg">
            Resize Image
          </h3>
        </div>

        {/* Controls Overlay - Bottom Third */}
        <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-white/50 dark:bg-black/50 backdrop-blur-[10px] p-6 flex flex-col justify-end">
          
          {/* Size Display */}
          <div className="mb-6 text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white drop-shadow-lg mb-2">
              {width}×{height}px
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Current: {currentWidth}×{currentHeight}px
            </div>
          </div>

          {/* Size Controls */}
          <div className="space-y-4 mb-6">
            
            {/* Width Control */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  Width
                </label>
                <input
                  type="number"
                  min="50"
                  max="800"
                  value={width}
                  onChange={(e) => handleWidthChange(parseInt(e.target.value) || 50)}
                  className="w-20 px-3 py-1 border border-gray-300 dark:border-gray-600/50 rounded-lg bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 backdrop-blur-[10px]"
                />
              </div>
              <input
                type="range"
                min="50"
                max="800"
                value={width}
                onChange={(e) => handleWidthChange(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-300 dark:bg-gray-600/30 rounded-lg appearance-none cursor-pointer slider-overlay"
              />
            </div>

            {/* Height Control */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  Height
                </label>
                <input
                  type="number"
                  min="50"
                  max="600"
                  value={height}
                  onChange={(e) => handleHeightChange(parseInt(e.target.value) || 50)}
                  className="w-20 px-3 py-1 border border-gray-300 dark:border-gray-600/50 rounded-lg bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 backdrop-blur-[10px]"
                />
              </div>
              <input
                type="range"
                min="50"
                max="600"
                value={height}
                onChange={(e) => handleHeightChange(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-300 dark:bg-gray-600/30 rounded-lg appearance-none cursor-pointer slider-overlay"
              />
            </div>

            {/* Aspect Ratio Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="aspect-ratio"
                checked={maintainAspectRatio}
                onChange={(e) => handleAspectRatioToggle(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="aspect-ratio" className="text-sm font-medium text-gray-800 dark:text-gray-200 cursor-pointer">
                Maintain aspect ratio
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-6 py-3 text-sm font-semibold text-gray-800 dark:text-gray-200 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600/50 rounded-xl hover:bg-white/60 dark:hover:bg-gray-700/60 backdrop-blur-[10px] transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 dark:from-blue-600/80 dark:to-purple-600/80 dark:hover:from-blue-700/90 dark:hover:to-purple-700/90 rounded-xl shadow-lg hover:shadow-xl backdrop-blur-[10px] transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Apply Resize</span>
            </button>
          </div>
        </div>
      </div>

      {/* Custom overlay slider styles */}
      <style jsx>{`
        .slider-overlay::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          cursor: pointer;
          box-shadow: 0 2px 12px rgba(59, 130, 246, 0.5);
          border: 2px solid white;
        }
        .slider-overlay::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 12px rgba(59, 130, 246, 0.5);
        }
        .slider-overlay::-webkit-slider-track {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px;
          height: 8px;
        }
        .slider-overlay::-moz-range-track {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px;
          height: 8px;
          border: none;
        }
        .slider-overlay:hover::-webkit-slider-track {
          background: rgba(255, 255, 255, 0.3);
        }
        .slider-overlay:hover::-moz-range-track {
          background: rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>,
    document.body
  )
}

export default ImageResizeModal
